import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { List, Pencil, RefreshCw, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { ragApi } from '../../../api/rag.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { KpiCard } from '../../../components/ui/KpiCard.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

import { UploadDocumentModal } from './UploadDocumentModal.jsx';
import { EditDocumentModal } from './EditDocumentModal.jsx';
import { ChunksDrawer } from './ChunksDrawer.jsx';
import { QueryPanel } from './QueryPanel.jsx';
import { SOURCE_TYPES, SOURCE_LABEL, STATUS_TONE, STATUS_LABEL } from './ragConstants.js';

function IconButton({ label, onClick, icon: Icon, danger }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`rounded-md p-1.5 text-ink-muted transition-colors ${danger ? 'hover:bg-danger/10 hover:text-danger' : 'hover:bg-surface-subtle hover:text-ink'}`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

export function RagPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const canUpload = hasPermission('rag:upload');
  const canDelete = hasPermission('rag:delete');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [reindexTarget, setReindexTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [chunksTarget, setChunksTarget] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const statsQuery = useQuery({ queryKey: ['rag', 'stats'], queryFn: ragApi.stats });
  const stats = statsQuery.data;

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['rag', 'documents'],
    fetcher: ragApi.list
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rag', 'documents'] });
    queryClient.invalidateQueries({ queryKey: ['rag', 'stats'] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => ragApi.remove(id),
    onSuccess: () => { toast.success('Documento eliminato.'); invalidate(); setConfirmTarget(null); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const archiveMutation = useMutation({
    mutationFn: (doc) => ragApi.update(doc._id, { active: !doc.active }),
    onSuccess: (_d, doc) => { toast.success(doc.active ? 'Documento archiviato.' : 'Documento ripristinato.'); invalidate(); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const inProgress = stats ? (stats.byStatus?.indexing || 0) + (stats.byStatus?.pending || 0) : 0;
  const failed = stats ? (stats.byStatus?.failed || 0) : 0;

  const columns = [
    {
      key: 'title',
      header: 'Documento',
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium text-ink">{r.title}</span>
          {r.filename && <span className="text-xs text-ink-faint">{r.filename}</span>}
        </div>
      )
    },
    { key: 'sourceType', header: 'Tipo', render: (r) => <Badge>{SOURCE_LABEL[r.sourceType] || r.sourceType}</Badge> },
    { key: 'language', header: 'Lingua', render: (r) => <span className="text-xs uppercase text-ink-muted">{r.language || '-'}</span> },
    { key: 'chunks', header: 'Chunk', align: 'right', render: (r) => <span className="tabular-nums">{fmt.int(r.chunkCount || 0)}</span> },
    {
      key: 'status',
      header: 'Stato',
      render: (r) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
          {r.active === false && <Badge tone="neutral">Nascosto</Badge>}
        </span>
      )
    },
    { key: 'createdAt', header: 'Caricato', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-0.5">
          <IconButton label="Vedi chunk" icon={List} onClick={() => setChunksTarget(r)} />
          {canUpload && <IconButton label="Modifica metadati" icon={Pencil} onClick={() => setEditTarget(r)} />}
          {canUpload && <IconButton label="Reindicizza (sostituisci file)" icon={RefreshCw} onClick={() => { setReindexTarget(r); setUploadOpen(true); }} />}
          {canUpload && (
            <IconButton
              label={r.active === false ? 'Ripristina' : 'Archivia'}
              icon={r.active === false ? ArchiveRestore : Archive}
              onClick={() => archiveMutation.mutate(r)}
            />
          )}
          {canDelete && <IconButton label="Elimina" icon={Trash2} danger onClick={() => setConfirmTarget(r)} />}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.rag')}
        description="Documenti, schede e procedure indicizzati per la ricerca semantica dell'assistente AI."
        actions={canUpload ? (
          <Button onClick={() => { setReindexTarget(null); setUploadOpen(true); }}>Carica documento</Button>
        ) : null}
      />

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Documenti"
          loading={statsQuery.isLoading}
          value={stats ? fmt.int(stats.total) : '-'}
          sub={failed ? `${failed} con errore` : 'Tutti elaborati'}
        />
        <KpiCard
          label="Pronti"
          loading={statsQuery.isLoading}
          value={stats ? fmt.int(stats.byStatus?.ready || 0) : '-'}
          sub={inProgress ? `${inProgress} in lavorazione` : 'Nessuno in coda'}
        />
        <KpiCard
          label="Chunk indicizzati"
          loading={statsQuery.isLoading}
          value={stats ? fmt.int(stats.chunks) : '-'}
          sub={stats && stats.tokens ? `~${fmt.int(stats.tokens)} token` : null}
        />
        <KpiCard
          label="Costo embedding"
          loading={statsQuery.isLoading}
          value={stats ? fmt.usd(stats.costUsd) : '-'}
          sub="Stima cumulativa"
        />
      </section>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface-elevated p-4">
        <Input
          className="w-64"
          label="Cerca"
          placeholder="titolo, tag..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
        <Select label="Stato" className="w-44" value={filters.status || ''} onChange={(e) => setFilters({ status: e.target.value || undefined })}>
          <option value="">Tutti</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <Select label="Tipo" className="w-48" value={filters.sourceType || ''} onChange={(e) => setFilters({ sourceType: e.target.value || undefined })}>
          <option value="">Tutti</option>
          {SOURCE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{
          title: 'Nessun documento',
          description: canUpload ? 'Carica il primo file (PDF, DOCX, Markdown, HTML, TXT) per popolare la knowledge base.' : 'Nessun documento indicizzato.',
          action: canUpload ? <Button onClick={() => { setReindexTarget(null); setUploadOpen(true); }}>Carica documento</Button> : null
        }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <QueryPanel />

      <UploadDocumentModal
        open={uploadOpen}
        onClose={() => { setUploadOpen(false); setReindexTarget(null); invalidate(); }}
        reindexTarget={reindexTarget}
      />

      <EditDocumentModal
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        document={editTarget}
      />

      <ChunksDrawer
        open={Boolean(chunksTarget)}
        onClose={() => setChunksTarget(null)}
        document={chunksTarget}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare il documento?"
        description={confirmTarget ? `"${confirmTarget.title}" e i suoi chunk saranno rimossi definitivamente dall'indice.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />
    </div>
  );
}
